import { useState } from 'react';
import { NavLink, Link } from 'react-router-dom';
import { 
  LayoutDashboard, 
  Building, 
  LogOut, 
  ChevronLeft, 
  ChevronRight,
  Menu,
  X,
  LineChart,
  Settings,
  FolderTree
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTheme } from '@/contexts/ThemeContext';
import { useAuth } from '@/contexts/AuthContext';
import { cn } from '@/lib/utils';
import { Separator } from '@/components/ui/separator';

export default function DashboardSidebar() {
  const [expanded, setExpanded] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const { theme } = useTheme();
  const { signOut } = useAuth();
  
  // Navigation items in the desired order - fixed for properties only
  const navigation = [
    { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard },
    { name: 'Imóveis', href: '/dashboard/listings', icon: Building },
    { name: 'Rastreamento', href: '/dashboard/tracking', icon: LineChart },
    { name: 'Configurações', href: '/dashboard/settings', icon: Settings },
  ];
  
  const toggleSidebar = () => {
    setExpanded(!expanded);
  };
  
  const toggleMobileSidebar = () => {
    setMobileOpen(!mobileOpen);
  };
  
  // Classes for navigation links
  const navItemClasses = ({ isActive }: { isActive: boolean }) => {
    return cn(
      "flex items-center space-x-3 py-2 px-3 rounded-md transition-colors",
      {
        "bg-primary/10 text-primary": isActive,
        "hover:bg-muted text-muted-foreground hover:text-foreground": !isActive,
      }
    );
  };

  // Logo URL based on theme
  const logoUrl = theme === 'dark' 
    ? 'https://tvalgxmzomdvttzuyznf.supabase.co/storage/v1/object/public/public/logos/vitrinelogo-white.png'
    : 'https://tvalgxmzomdvttzuyznf.supabase.co/storage/v1/object/public/public/logos/vitrinelogo-black.png';
  
  // Sidebar content
  const sidebarContent = (
    <>
      <div className="flex items-center justify-between p-4">
        <NavLink to="/" className="flex items-center space-x-2">
          <img 
            src={logoUrl} 
            alt="Logo" 
            className="h-13"
          />
        </NavLink>
        
        {/* Mobile close button */}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={toggleMobileSidebar}
          className="md:hidden"
        >
          <X className="h-5 w-5" />
        </Button>
        
        {/* Desktop expand/collapse button */}
        <Button 
          variant="ghost" 
          size="icon" 
          onClick={toggleSidebar}
          className="hidden md:flex"
        >
          {expanded ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
        </Button>
      </div>
      
      <div className="px-2 py-2">
        <nav className="space-y-1 flex flex-col">
          {navigation.map((item) => (
            <NavLink
              key={item.name}
              to={item.href}
              className={navItemClasses}
            >
              <item.icon className="h-5 w-5" />
              {expanded && <span>{item.name}</span>}
            </NavLink>
          ))}
        </nav>
      </div>
      
      <div className="mt-auto p-4">
        <Separator className="mb-4" />
        <button 
          onClick={() => signOut()} 
          className="flex items-center space-x-3 py-2 w-full text-left text-muted-foreground hover:text-destructive transition-colors"
        >
          <LogOut className="h-5 w-5" />
          {expanded && <span>Sair</span>}
        </button>
      </div>
    </>
  );
  
  return (
    <>
      {/* Mobile trigger */}
      <Button 
        variant="outline" 
        size="icon" 
        className="fixed top-3 left-4 z-50 md:hidden"
        onClick={toggleMobileSidebar}
      >
        <Menu className="h-5 w-5" />
      </Button>
      
      {/* Mobile overlay */}
      <div 
        className={cn(
          "fixed inset-0 bg-black/50 z-40 md:hidden transition-opacity",
          {
            "opacity-100": mobileOpen,
            "opacity-0 pointer-events-none": !mobileOpen,
          }
        )}
        onClick={toggleMobileSidebar}
      />
      
      {/* Mobile sidebar */}
      <div 
        className={cn(
          "fixed inset-y-0 left-0 w-64 bg-background border-r z-50 transition-transform duration-300 md:hidden",
          {
            "translate-x-0": mobileOpen,
            "-translate-x-full": !mobileOpen,
          }
        )}
      >
        {sidebarContent}
      </div>
      
      {/* Desktop sidebar */}
      <div 
        className={cn(
          "hidden md:flex flex-col h-screen border-r bg-background transition-all duration-300",
          {
            "w-64": expanded,
            "w-16": !expanded,
          }
        )}
      >
        {sidebarContent}
      </div>
    </>
  );
}